import React, { useState, useEffect, useRef } from 'react';
import './CreatePrimeSessonModel.css';
import { toast } from 'react-hot-toast';
import { axiosInstance } from '../../../../utils/tokenUtils';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import CalendarTodayIcon from '../../../../assets/ScheduleIcon/calendar.svg';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Chip, Autocomplete, TextField } from '@mui/material';
import DropdownIcon from '../../../../assets/ScheduleIcon/dropdown.svg';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import ReactDOM from 'react-dom';

// Import time picker components
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers';

// Setup dayjs plugins for timezone handling
dayjs.extend(utc);
dayjs.extend(timezone);

// Custom renderer that hides the minute hand in the hours view
const customTimeViewRenderer = props => {
  const { view, value, onChange, ampm, ...other } = props;

  // For hours view, render the standard clock
  if (view === 'hours') {
    return renderTimeViewClock({
      view,
      value,
      onChange,
      ampm,
      ...other,
    });
  }

  // For minutes view, which is shown after hours are selected
  return renderTimeViewClock({
    view,
    value,
    onChange,
    ampm,
    ...other,
  });
};

const CreatePrimeSessionModel = ({ open, onClose }) => {
  const [subject, setSubject] = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [level, setLevel] = useState('');
  const [selectedDates, setSelectedDates] = useState([]);
  const [fromTime, setFromTime] = useState('');
  const [toTime, setToTime] = useState('');
  const [description, setDescription] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [sessionType, setSessionType] = useState('Prime');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [selectedTags, setSelectedTags] = useState([]);
  const [inputTag, setInputTag] = useState('');

  // States for time picker
  const [activeTimePicker, setActiveTimePicker] = useState(null);
  const [clockView, setClockView] = useState('hours'); // 'hours' or 'minutes'

  // Add refs for time picker positioning
  const timePickerRefs = useRef({});

  // New states for API integration
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [existingAvailability, setExistingAvailability] = useState([]);
  const [timeConflicts, setTimeConflicts] = useState([]);

  // Teacher profile data
  const [teacherProfile, setTeacherProfile] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [levels, setLevels] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [primeSessionTimeFrame, setPrimeSessionTimeFrame] = useState(null);

  // Add a ref for the calendar button
  const calendarButtonRef = useRef(null);

  useEffect(() => {
    if (!open) {
      resetForm();
    } else {
      setCurrentDate(new Date());
      setCalendarDate(new Date());
      setSelectedDates([]);
      fetchTeacherProfile();
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      fetchExistingAvailability();
    }
  }, [open]);

  // New useEffect to handle "fromTime" change
  useEffect(() => {
    if (fromTime) {
      // Parse the fromTime using dayjs
      const fromTimeObj = stringToTime(fromTime);
      if (fromTimeObj) {
        // Add 2 hours to the fromTime
        const toTimeObj = fromTimeObj.add(2, 'hours');
        // Format and set the toTime
        setToTime(toTimeObj.format('hh:mm A'));
      }
    } else {
      // If fromTime is cleared, also clear toTime
      setToTime('');
    }
  }, [fromTime]);

  const fetchTeacherProfile = async () => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.get('/teachers');
      const profileData = response.data;

      setTeacherProfile(profileData);

      // Set subjects
      const subjectOptions = profileData.subjects.map(sub => ({
        value: sub.title,
        label: sub.title,
        id: sub.id,
        tags: sub.tags,
      }));
      setSubjects(subjectOptions);

      // Extract levels of teaching
      const levelOptions = profileData.level_of_teaching.map(level => {
        // Convert snake_case to Title Case
        const formattedLevel = level
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        return {
          value: level, // Keep original snake_case for API
          label: formattedLevel, // Display formatted version
        };
      });
      setLevels(levelOptions);

      // Set prime session time frame
      setPrimeSessionTimeFrame(profileData.prime_session_time_frame);

      setIsLoading(false);
    } catch (error) {
      console.error('Failed to fetch teacher profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to load teacher profile');
      setIsLoading(false);
    }
  };

  // Update available tags when subject changes
  useEffect(() => {
    if (subject && teacherProfile) {
      const selectedSubject = teacherProfile.subjects.find(s => s.title === subject);

      if (selectedSubject) {
        setAvailableTags(selectedSubject.tags);
        setSubjectId(selectedSubject.id);
      } else {
        setAvailableTags([]);
        setSubjectId('');
      }

      // Reset selected tags when subject changes
      setSelectedTags([]);
    }
  }, [subject, teacherProfile]);

  const resetForm = () => {
    setSubject('');
    setSubjectId('');
    setLevel('');
    setSelectedDates([]);
    setFromTime('');
    setToTime('');
    setDescription('');
    setShowCalendar(false);
    setShowConfirmation(false);
    setCurrentDate(new Date());
    setCalendarDate(new Date());
    setSelectedTags([]);
    setInputTag('');
  };

  // Utility function to convert string time to dayjs object
  const stringToTime = timeString => {
    if (!timeString) return null;
    // Parse time including AM/PM format
    return dayjs(`2023-01-01 ${timeString}`, 'YYYY-MM-DD hh:mm A');
  };

  // Function to handle opening a time picker
  const handleTimePickerOpen = field => {
    // Only allow opening the "From" time picker
    if (field !== 'from') return;

    // Reset the current field's value when opening the picker to allow re-selection
    if (fromTime) {
      setActiveTimePicker({
        field,
        previousValue: fromTime, // Store previous value
      });
    } else {
      setActiveTimePicker({ field });
    }
    setClockView('hours'); // Always start with hours view
  };

  // Function to handle closing a time picker
  const handleTimePickerClose = () => {
    // If user cancels without selecting a new value, restore the previous value
    if (activeTimePicker?.previousValue) {
      const { field, previousValue } = activeTimePicker;

      // Only restore if the value was actually cleared (user didn't select a new time)
      if (field === 'from' && !fromTime) {
        setFromTime(previousValue);
      } else if (field === 'to' && !toTime) {
        setToTime(previousValue);
      }
    }

    setActiveTimePicker(null);
    setClockView('hours'); // Reset to hours view
  };

  // Modified handleTimeChange to handle sequential time selection
  const handleTimeChange = (field, value) => {
    // Only allow changes to the 'from' field - 'to' time is calculated automatically
    if (field !== 'from') return;

    // If value is null, the field is being cleared
    if (!value) {
      setFromTime('');
      // toTime will be cleared by the useEffect
      setClockView('hours'); // Reset to hours view
      return;
    }

    // For hours selection, update the hours and keep existing minutes or use 00
    if (clockView === 'hours') {
      // If we already have a time value, extract the existing minutes
      if (fromTime) {
        const existingTime = stringToTime(fromTime);
        if (existingTime) {
          // Keep the existing minutes but update the hours
          const updatedTime = value.minute(existingTime.minute());
          const formattedTime = updatedTime.format('hh:mm A');
          setFromTime(formattedTime);
        } else {
          // If parsing failed, use 00 minutes with the new hour
          const formattedTime = value.minute(0).format('hh:mm A');
          setFromTime(formattedTime);
        }
      } else {
        // If no existing time, use 00 minutes with the new hour
        const formattedTime = value.minute(0).format('hh:mm A');
        setFromTime(formattedTime);
      }

      // Switch to minutes view to let user select minutes if desired
      setClockView('minutes');
      return;
    }

    // For minutes selection, format and update the complete time
    const formattedTime = value.format('hh:mm A');
    setFromTime(formattedTime);

    // Reset to hours view for next selection
    setClockView('hours');
  };

  // Function to convert UTC time to local time
  const convertUTCToLocal = timeString => {
    if (!timeString) return null;
    // Assuming timeString is in 'HH:mm' format
    const [hours, minutes] = timeString.split(':');
    const utcDate = dayjs.utc().hour(hours).minute(minutes);
    return utcDate.local().format('HH:mm');
  };

  // Function to convert local time to UTC
  const convertLocalToUTC = timeString => {
    if (!timeString) return null;
    // Assuming timeString is in 'HH:mm' format or '12-hour format with AM/PM'
    let localDate;
    if (timeString.includes('AM') || timeString.includes('PM')) {
      localDate = dayjs(`2023-01-01 ${timeString}`, 'YYYY-MM-DD hh:mm A');
    } else {
      const [hours, minutes] = timeString.split(':');
      localDate = dayjs().hour(hours).minute(minutes);
    }
    return localDate.utc().format('HH:mm');
  };

  // Utility function to convert time string to minutes
  const convertToMinutes = timeString => {
    if (!timeString) return 0;
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Convert time string to UTC
  const convertTimeToUTC = timeString => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const localTime = dayjs().hour(hours).minute(minutes);
    return localTime.utc().format('HH:mm');
  };

  // Convert UTC time string to local
  const convertUTCTimeToLocal = timeString => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const utcTime = dayjs.utc().hour(hours).minute(minutes);
    return utcTime.local().format('HH:mm');
  };

  const fetchExistingAvailability = async () => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.get('/teachers/availability?session_type=prime');

      // Support both old array and new paginated shape { count, results: [] }
      const payload = response.data?.data || response.data;
      const items = Array.isArray(payload) ? payload : payload?.results || [];

      // Convert UTC times to local times, considering both availability and overrides (prime uses overrides)
      const localizedAvailability = items.map(item => {
        // Prime sessions may have empty availability and use overrides per date
        const availabilitySlots = (item.availability || []).map(slot => {
          const localStartTime = convertUTCToLocal(slot.startTime);
          const localEndTime = convertUTCToLocal(slot.endTime);
          const startMinutes = convertToMinutes(slot.startTime);
          const endMinutes = convertToMinutes(slot.endTime);
          const isOvernightShift = endMinutes < startMinutes;
          return {
            ...slot,
            startTime: localStartTime,
            endTime: localEndTime,
            isOvernightShift,
          };
        });

        const overrideSlots = (item.overrides || []).map(ov => ({
          date: ov.date,
          startTime: convertUTCToLocal(ov.startTime),
          endTime: convertUTCToLocal(ov.endTime),
        }));

        return {
          ...item,
          availability: availabilitySlots,
          overrides: overrideSlots,
        };
      });

      setExistingAvailability(localizedAvailability);
      setIsLoading(false);
    } catch (error) {
      console.error('Failed to fetch availability:', error);
      toast.error(error.response?.data?.detail || 'Failed to load existing availability data');
      setIsLoading(false);
    }
  };

  const isDateInTimeFrame = date => {
    if (!primeSessionTimeFrame) return true;

    const startDate = new Date(primeSessionTimeFrame.start_date);
    const endDate = new Date(primeSessionTimeFrame.end_date);

    // Add check for past dates
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Normalize the date for comparison
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);

    // Check if date is within time frame AND not in the past
    return checkDate >= today && checkDate >= startDate && checkDate <= endDate;
  };

  const handleDateClick = date => {
    // Check if date is within the allowed time frame
    if (!isDateInTimeFrame(date)) {
      toast.error('Date is outside the allowed prime session time frame');
      return;
    }

    const dateString = date.toDateString();

    // If already selected, remove it
    if (selectedDates.some(d => new Date(d).toDateString() === dateString)) {
      setSelectedDates(selectedDates.filter(d => new Date(d).toDateString() !== dateString));
    } else {
      // Check which week this date belongs to
      const weekNumber = Math.floor((date - currentDate) / (7 * 24 * 60 * 60 * 1000)) + 1;

      // Count how many dates are already selected for this week
      const week1Dates = selectedDates.filter(d => {
        const wkNum = Math.floor((new Date(d) - currentDate) / (7 * 24 * 60 * 60 * 1000)) + 1;
        return wkNum === 1;
      });

      const week2Dates = selectedDates.filter(d => {
        const wkNum = Math.floor((new Date(d) - currentDate) / (7 * 24 * 60 * 60 * 1000)) + 1;
        return wkNum === 2;
      });

      // Check if adding this date would exceed the 3-date limit for the week
      if (
        (weekNumber === 1 && week1Dates.length >= 3) ||
        (weekNumber === 2 && week2Dates.length >= 3)
      ) {
        toast.error(`You can select at most 3 dates for Week ${weekNumber}`);
        return;
      }

      setSelectedDates([...selectedDates, date]);
    }
  };

  const handleCalendarDateSelect = date => {
    setCurrentDate(date);
    setSelectedDates([]);
    setShowCalendar(false);
  };

  const handlePrevWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentDate(newDate);
  };

  const handleNextWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentDate(newDate);
  };

  const checkForTimeConflicts = () => {
    if (!selectedDates.length) return [];

    const conflicts = [];

    selectedDates.forEach(selectedDate => {
      const day = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][
        selectedDate.getDay()
      ];

      existingAvailability.forEach(availability => {
        availability.availability.forEach(slot => {
          if (slot.days.includes(day)) {
            // Check if time slots overlap
            if (
              isTimeOverlapping(
                fromTime,
                toTime,
                slot.startTime,
                slot.endTime,
                slot.isOvernightShift
              )
            ) {
              conflicts.push({
                date: selectedDate,
                conflictTime: `${slot.startTime} - ${slot.endTime}`,
              });
            }
          }
        });
      });
    });

    return conflicts;
  };

  const isTimeOverlapping = (newStart, newEnd, existingStart, existingEnd, existingIsOvernight) => {
    // Convert times to minutes for easier comparison
    const newStartMinutes = convertToMinutes(newStart);
    const newEndMinutes = convertToMinutes(newEnd);
    const existingStartMinutes = convertToMinutes(existingStart);
    const existingEndMinutes = convertToMinutes(existingEnd);

    // Check if newStart to newEnd spans overnight (newEnd < newStart)
    const isNewOvernight = newEndMinutes < newStartMinutes;
    // Use the provided flag for existing overnight shifts or fallback to checking minutes
    const isExistingOvernight = existingIsOvernight || existingEndMinutes < existingStartMinutes;

    if (isNewOvernight && isExistingOvernight) {
      // Both are overnight sessions - they always overlap in some way
      return true;
    } else if (isNewOvernight) {
      // New session is overnight, existing is normal
      // It overlaps if existing start OR end falls between new start and end of next day
      return (
        existingStartMinutes >= newStartMinutes ||
        existingStartMinutes <= newEndMinutes ||
        existingEndMinutes >= newStartMinutes ||
        existingEndMinutes <= newEndMinutes
      );
    } else if (isExistingOvernight) {
      // Existing session is overnight, new is normal
      // It overlaps if new start OR end falls between existing start and end of next day
      return (
        newStartMinutes >= existingStartMinutes ||
        newStartMinutes <= existingEndMinutes ||
        newEndMinutes >= existingStartMinutes ||
        newEndMinutes <= existingEndMinutes
      );
    } else {
      // Neither session is overnight - normal overlap check
      return (
        (newStartMinutes >= existingStartMinutes && newStartMinutes < existingEndMinutes) ||
        (newEndMinutes > existingStartMinutes && newEndMinutes <= existingEndMinutes) ||
        (newStartMinutes <= existingStartMinutes && newEndMinutes >= existingEndMinutes)
      );
    }
  };

  const validateForm = () => {
    if (!subject) return 'Please select a subject';
    if (!level) return 'Please select a level to teach';
    if (selectedDates.length === 0) return 'Please select at least one date';
    if (!fromTime) return 'Please select a start time';
    if (!toTime) return 'Please select an end time';
    if (!description) return 'Please provide a short description';

    // We're no longer validating that end time must be after start time
    // Any case where toTime < fromTime is now considered an overnight session

    return null;
  };

  const formatDateForAPI = date => {
    const d = new Date(date);
    // Format to local date first
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');

    // Create UTC datetime string
    return `${year}-${month}-${day}T00:00:00Z`;
  };

  const validateSelectedDates = () => {
    // Count dates in each week
    const week1Dates = selectedDates.filter(date => {
      const weekNumber = Math.floor((date - currentDate) / (7 * 24 * 60 * 60 * 1000)) + 1;
      return weekNumber === 1;
    });

    const week2Dates = selectedDates.filter(date => {
      const weekNumber = Math.floor((date - currentDate) / (7 * 24 * 60 * 60 * 1000)) + 1;
      return weekNumber === 2;
    });

    if (week1Dates.length !== 3) {
      return 'You must select exactly 3 dates for week 1';
    }

    if (week2Dates.length !== 3) {
      return 'You must select exactly 3 dates for week 2';
    }

    return null;
  };

  const handleTagChange = (event, newTags) => {
    setSelectedTags(newTags);
  };

  const handleTagInputChange = (event, newInputValue) => {
    setInputTag(newInputValue);
  };

  const handleTagDelete = tagToDelete => {
    setSelectedTags(selectedTags.filter(tag => tag.id !== tagToDelete.id));
  };

  const getLevelApiValue = displayLevel => {
    // Find the level object with matching label
    const levelObj = levels.find(l => l.label === displayLevel);
    return levelObj ? levelObj.value : displayLevel.toLowerCase().replace(/\s+/g, '_');
  };

  const handleConfirm = async () => {
    setError('');
    setTimeConflicts([]);

    // Validate form
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    // Validate selected dates distribution
    const datesValidationError = validateSelectedDates();
    if (datesValidationError) {
      setError(datesValidationError);
      return;
    }

    // Check for time conflicts
    const conflicts = checkForTimeConflicts();
    if (conflicts.length > 0) {
      setTimeConflicts(conflicts);
      setError('There are time conflicts with existing sessions');
      return;
    }

    try {
      setIsLoading(true);

      // Separate selected dates into week1 and week2
      const week1Dates = [];
      const week2Dates = [];

      selectedDates.forEach(date => {
        const dateString = formatDateForAPI(date);

        // Check if date belongs to week 1 or week 2
        const weekNumber = Math.floor((date - currentDate) / (7 * 24 * 60 * 60 * 1000)) + 1;
        if (weekNumber <= 1) {
          week1Dates.push(dateString);
        } else {
          week2Dates.push(dateString);
        }
      });

      // Get the level value for API
      const levelApiValue = getLevelApiValue(level);

      // Parse the local times and check if this is an overnight session
      const fromTimeObj = stringToTime(fromTime);
      const toTimeObj = stringToTime(toTime);
      const isOvernightSession = toTimeObj && fromTimeObj && toTimeObj.isBefore(fromTimeObj);

      // Convert to UTC
      const fromTimeUTC = fromTimeObj ? convertLocalToUTC(fromTime) : null;
      let toTimeUTC;

      // For overnight shifts, add a day to the end time before converting to UTC
      if (isOvernightSession) {
        const toTimeNextDay = toTimeObj.add(1, 'day');
        toTimeUTC = toTimeNextDay.utc().format('HH:mm');
      } else {
        toTimeUTC = toTimeObj ? convertLocalToUTC(toTime) : null;
      }

      // Format time slots as a list with a single object
      const timeSlots = [
        {
          start_time: fromTimeUTC,
          end_time: toTimeUTC,
        },
      ];

      const requestBody = {
        subject: subjectId,
        levels_to_teach: levelApiValue,
        description: description,
        week1_dates: {
          selected_dates: week1Dates,
        },
        week2_dates: {
          selected_dates: week2Dates,
        },
        time_slots: timeSlots,
        tags: selectedTags.map(tag => tag.id),
      };

      console.log('Sending request to create prime session:', requestBody);
      await axiosInstance.post('/teachers/availability/prime', requestBody);

      setIsLoading(false);
      toast.success('Prime session created successfully!');

      // Soft refresh calendar widgets without reloading the page
      try {
        window.dispatchEvent(
          new CustomEvent('calendar:refresh', { detail: { source: 'prime-create' } })
        );
      } catch {}

      // Quietly refetch availability and push to main page without showing any loader
      (async () => {
        try {
          const res = await axiosInstance.get('/teachers/availability?session_type=prime');
          const payload = res.data?.data || res.data;
          const items = Array.isArray(payload) ? payload : payload?.results || [];
          if (typeof window.updateAvailabilityCards === 'function') {
            window.updateAvailabilityCards(items);
          }
          window.dispatchEvent(
            new CustomEvent('availability:update', {
              detail: { sessionType: 'prime', items },
            })
          );
        } catch {
          /* silent */
        }
      })();

      // Close modal immediately and reset local state
      resetForm();
      onClose();
    } catch (error) {
      setIsLoading(false);
      console.error('Failed to create prime session:', error);

      if (error.response?.data?.detail) {
        // Format validation errors from API response
        const details = error.response.data.detail;
        let errorMessage = '';

        if (Array.isArray(details)) {
          errorMessage = 'Request validation failed: ';
          details.forEach(detail => {
            errorMessage += `\n- ${detail.msg}`;
          });
        } else if (typeof details === 'string') {
          errorMessage = details;
        } else if (typeof details === 'object') {
          errorMessage = JSON.stringify(details);
        } else {
          errorMessage = 'Unknown error occurred';
        }

        setError(errorMessage);
        toast.error(errorMessage);
      } else {
        setError('Failed to create prime session. Please try again.');
        toast.error('Failed to create prime session. Please try again.');
      }
    }
  };

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
    onClose(); // Close the entire modal
  };

  const getDayName = dayNum => {
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return dayNames[dayNum];
  };

  const generateWeekDays = (startDate, weekNumber) => {
    const days = [];
    const currentDay = new Date(startDate);

    // Start from the current selected date for week 1
    // For week 2, add 7 days to the current date
    if (weekNumber === 2) {
      currentDay.setDate(currentDay.getDate() + 7);
    }

    // Generate 7 days starting from the current date
    for (let i = 0; i < 7; i++) {
      const date = new Date(currentDay);
      days.push({
        date,
        day: date.getDate(),
        dayName: getDayName(date.getDay()),
        isInTimeFrame: isDateInTimeFrame(date),
      });
      currentDay.setDate(currentDay.getDate() + 1);
    }

    return days;
  };

  const renderCalendarMonth = () => {
    const year = calendarDate.getFullYear();
    const month = calendarDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();

    const firstDayOfWeek = firstDay.getDay();

    const prevMonthLastDay = new Date(year, month, 0).getDate();

    const days = [];

    for (let i = 0; i < firstDayOfWeek; i++) {
      const day = prevMonthLastDay - firstDayOfWeek + i + 1;
      const date = new Date(year, month - 1, day);
      days.push({
        day,
        month: month - 1,
        year,
        isCurrentMonth: false,
        date: date,
        isInTimeFrame: isDateInTimeFrame(date),
      });
    }

    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month, i);
      days.push({
        day: i,
        month,
        year,
        isCurrentMonth: true,
        date: date,
        isInTimeFrame: isDateInTimeFrame(date),
      });
    }

    const remainingDays = 7 - (days.length % 7 || 7);
    for (let i = 1; i <= remainingDays; i++) {
      const date = new Date(year, month + 1, i);
      days.push({
        day: i,
        month: month + 1,
        year,
        isCurrentMonth: false,
        date: date,
        isInTimeFrame: isDateInTimeFrame(date),
      });
    }

    const weeks = [];
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7));
    }

    // Calculate the position for the calendar popup
    const calculatePosition = () => {
      if (!calendarButtonRef.current) return {};

      const buttonRect = calendarButtonRef.current.getBoundingClientRect();
      const topPosition = buttonRect.bottom + 5; // 5px offset
      const rightPosition = window.innerWidth - buttonRect.right;

      return {
        top: `${topPosition}px`,
        right: `${rightPosition}px`,
      };
    };

    const calendarStyle = calculatePosition();

    // Return the calendar to be rendered in a portal
    return ReactDOM.createPortal(
      <div className="calendar-popup" style={calendarStyle}>
        <div className="calendar-header">
          <button onClick={() => setCalendarDate(new Date(year, month - 1, 1))}>&lt;</button>
          <div className="calendar-title">
            <div>{new Intl.DateTimeFormat('en-US', { month: 'long' }).format(calendarDate)}</div>
            <div className="calendar-year">{year}</div>
          </div>
          <button onClick={() => setCalendarDate(new Date(year, month + 1, 1))}>&gt;</button>
        </div>
        <div className="calendar-days-header">
          <div>S</div>
          <div>M</div>
          <div>T</div>
          <div>W</div>
          <div>T</div>
          <div>F</div>
          <div>S</div>
        </div>
        <div className="calendar-grid">
          {weeks.map((week, weekIndex) => (
            <div key={weekIndex} className="calendar-week">
              {week.map((day, dayIndex) => {
                const isSelected = day.date.toDateString() === currentDate.toDateString();

                return (
                  <div
                    key={dayIndex}
                    className={`calendar-day ${!day.isCurrentMonth ? 'other-month' : ''} ${isSelected ? 'selected' : ''} ${!day.isInTimeFrame ? 'disabled' : ''}`}
                    onClick={() => day.isInTimeFrame && handleCalendarDateSelect(day.date)}
                  >
                    {day.day}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>,
      document.body
    );
  };

  // Add a function to render the time picker as a portal
  const renderTimePickerPortal = () => {
    if (!activeTimePicker) return null;

    const { field } = activeTimePicker;
    const timeValue = field === 'from' ? stringToTime(fromTime) : stringToTime(toTime);

    // Get the reference for this time picker
    const inputElement = timePickerRefs.current[field];

    return ReactDOM.createPortal(
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <TimePicker
          value={timeValue}
          onChange={newValue => handleTimeChange(field, newValue)}
          onClose={handleTimePickerClose}
          className="time-picker-portal"
          ampm={true}
          format="hh:mm A"
          minutesStep={15}
          open={true}
          viewRenderers={{
            hours: customTimeViewRenderer,
            minutes: customTimeViewRenderer,
          }}
          views={['hours', 'minutes']}
          view={clockView}
          onViewChange={newView => setClockView(newView)}
          slots={{
            textField: () => null, // Hide the default text field
          }}
          slotProps={{
            popper: {
              style: { zIndex: 2500 },
              placement: 'bottom-start',
              anchorEl: inputElement,
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, 5],
                  },
                },
              ],
            },
          }}
        />
      </LocalizationProvider>,
      document.body
    );
  };

  if (!open) return null;

  return (
    <div className="modal-overlay">
      {/* Main modal content */}
      {!showConfirmation && (
        <div className="modal">
          <div className="modal-header">
            <h2>Add a New Prime Session</h2>
            <button className="close-button" onClick={onClose}>
              <CloseIcon />
            </button>
          </div>
          <div className="modal-content">
            {/* {error && <div className="error-message">{error}</div>} */}

            <div className="form-group">
              <label>Subject</label>
              <div className="select-wrapper">
                <select
                  value={subject}
                  onChange={e => {
                    setSubject(e.target.value);
                    setError('');
                  }}
                  className="form-control"
                  disabled={isLoading}
                >
                  <option value="" disabled>
                    Select
                  </option>
                  {teacherProfile?.subjects.map((sub, index) => (
                    <option key={index} value={sub.title}>
                      {sub.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Tags</label>
              <Autocomplete
                multiple
                id="tags-filled"
                options={availableTags || []}
                getOptionLabel={option => option.title || ''}
                value={selectedTags}
                onChange={handleTagChange}
                inputValue={inputTag}
                onInputChange={handleTagInputChange}
                renderTags={() => null}
                renderInput={params => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder={subject ? 'Type here...' : 'Select a subject first'}
                    className="tags-input"
                  />
                )}
                disabled={!subject || isLoading}
                className="tags-autocomplete"
                filterSelectedOptions
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
              {selectedTags.length > 0 && (
                <div className="tags-container">
                  {selectedTags.map(tag => (
                    <Chip
                      key={tag.id}
                      label={tag.title}
                      onDelete={() => handleTagDelete(tag)}
                      className="tag-chip"
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Level to Teach</label>
              <div className="select-wrapper">
                <select
                  value={level}
                  onChange={e => {
                    setLevel(e.target.value);
                    setError('');
                  }}
                  className="form-control"
                  disabled={isLoading}
                >
                  <option value="" disabled>
                    Select
                  </option>
                  {levels.map((lvl, index) => (
                    <option key={index} value={lvl.label}>
                      {lvl.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group calendar-section">
              <label>Choose your Dates</label>

              <div className="calendar-navigation">
                <div className="week-nav">
                  <button className="nav-arrow" onClick={handlePrevWeek}>
                    <KeyboardArrowLeftIcon />
                  </button>
                  <button className="nav-arrow" onClick={handleNextWeek}>
                    <KeyboardArrowRightIcon />
                  </button>
                </div>
                <button
                  className="calendar-picker-btn"
                  onClick={() => setShowCalendar(!showCalendar)}
                  ref={calendarButtonRef}
                >
                  <img src={CalendarTodayIcon} alt="calendar" />
                </button>
                {showCalendar && renderCalendarMonth()}
              </div>

              <div className="calendar-container">
                <div className="week-container">
                  <div className="week-label">Week 01 (3 days required)</div>
                  <div className="days-container">
                    {generateWeekDays(currentDate, 1).map((day, index) => {
                      const isSelected = selectedDates.some(
                        d => new Date(d).toDateString() === day.date.toDateString()
                      );
                      return (
                        <div
                          key={index}
                          className={`day-item ${isSelected ? 'selected' : ''} ${!day.isInTimeFrame ? 'disabled' : ''}`}
                          onClick={() => day.isInTimeFrame && handleDateClick(day.date)}
                        >
                          <div className="day-number-prime">{day.day}</div>
                          <div className="day-name">{day.dayName}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="week-container">
                  <div className="week-label">Week 02 (3 days required)</div>
                  <div className="days-container">
                    {generateWeekDays(currentDate, 2).map((day, index) => {
                      const isSelected = selectedDates.some(
                        d => new Date(d).toDateString() === day.date.toDateString()
                      );
                      return (
                        <div
                          key={index}
                          className={`day-item ${isSelected ? 'selected' : ''} ${!day.isInTimeFrame ? 'disabled' : ''}`}
                          onClick={() => day.isInTimeFrame && handleDateClick(day.date)}
                        >
                          <div className="day-number-prime">{day.day}</div>
                          <div className="day-name">{day.dayName}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            <div className="form-group">
              <label>Select Your Timings</label>
              <div className="time-section">
                <div className="time-inputs">
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <div className="time-input-group">
                      <span className="time-label">From</span>
                      <div
                        className="time-input-wrapper"
                        onClick={() => handleTimePickerOpen('from')}
                        ref={el => {
                          // Store the ref for this time input field
                          timePickerRefs.current['from'] = el;
                        }}
                      >
                        <div className="time-input-icon">
                          <AccessTimeIcon fontSize="small" />
                        </div>
                        <input
                          className="time-input-field"
                          value={fromTime || ''}
                          placeholder="Select time"
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="time-input-group">
                      <span className="time-label">To</span>
                      <div
                        onClick={() => handleTimePickerOpen('to')}
                        ref={el => {
                          // Store the ref for this time input field
                          timePickerRefs.current['to'] = el;
                        }}
                        className="time-input-wrapper disabled"
                      >
                        <div className="time-input-icon">
                          <AccessTimeIcon fontSize="small" />
                        </div>
                        <input
                          className="time-input-field"
                          value={toTime || ''}
                          placeholder="Auto-set (2 hours after start)"
                          readOnly
                          disabled={true} // To is disabled because it's calculated from fromTime
                        />
                      </div>
                    </div>
                  </LocalizationProvider>
                </div>
              </div>
            </div>

            <div className="form-group">
              <label>Write a short description</label>
              <textarea
                value={description}
                onChange={e => {
                  setDescription(e.target.value);
                  setError('');
                }}
                className="form-control description-input"
                disabled={isLoading}
                rows={4}
              />
            </div>
          </div>
          <div className="modal-footer">
            <button className="cancel-button-prime" onClick={onClose}>
              Cancel
            </button>
            <button className="confirm-button-prime" onClick={handleConfirm} disabled={isLoading}>
              {isLoading ? 'Creating...' : 'Confirm'}
            </button>
          </div>

          {/* Add the time picker portal */}
          {renderTimePickerPortal()}
        </div>
      )}

      {/* Confirmation modal shown on success */}
      {showConfirmation && (
        <div className="confirmation-modal">
          <div className="confirmation-content">
            <button className="close-button" onClick={handleCloseConfirmation}>
              ×
            </button>
            <div className="confirmation-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="11" stroke="#0066FF" strokeWidth="2" />
                <path
                  d="M7 12L10 15L17 8"
                  stroke="#0066FF"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h2>New {sessionType} session Added Successfully!</h2>
            <p>
              Your availability has been updated. Students can now view your schedule and book
              sessions during your available times.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreatePrimeSessionModel;
